import type { ApiEnvId } from '@/deploy/services/api-pterodactyl-config.service';
import type { ApiCategory, ApiService } from '@/deploy/services/api-services.service';

// ============================================================
// Envs des APIs, miroir de ApiEnvs côté CICD.
//
// Dev et Bêta partagent les mêmes serveurs API : "dev" est l'env canonique du groupe
// Dev + Bêta. C'est lui qu'on lit (versions, endpoints, contrôles Ptero) et on l'affiche
// sur les deux onglets. Le store deployEnv reste à 4 valeurs (commun à d'autres pages).
// ============================================================

export const API_ENVS: ApiEnvId[] = ['dev', 'beta', 'preprod', 'prod'];

export const API_ENV_LABELS: Record<ApiEnvId, string> = { dev: 'Dev', beta: 'Bêta', preprod: 'Pré-prod', prod: 'Prod' };

/** Un env canonique par groupe : Dev + Bêta, Pré-prod, Prod. */
export const API_ENV_GROUPS: ApiEnvId[] = ['dev', 'preprod', 'prod'];

export function canonicalApiEnv(env: ApiEnvId): ApiEnvId {
  return env === 'beta' ? 'dev' : env;
}

/** Envs qui partagent les serveurs de `env` : [dev, beta] pour dev et beta. */
export function apiEnvGroup(env: ApiEnvId): ApiEnvId[] {
  return canonicalApiEnv(env) === 'dev' ? ['dev', 'beta'] : [env];
}

/** Libellé du groupe d'un env ; accepte une chaîne brute (résultats backend). */
export function apiGroupLabel(env: string): string {
  switch (env) {
    case 'dev':
    case 'beta': return 'Dev + Bêta';
    case 'preprod': return 'Pré-prod';
    case 'prod': return 'Prod';
    default: return env;
  }
}

/** Catégorie normalisée : absente (backend plus ancien) = API de jeu. */
export function apiCategory(api: Pick<ApiService, 'category'>): ApiCategory {
  return api.category === 'external' ? 'external' : 'ig';
}

export function isExternalApi(api: Pick<ApiService, 'category'>): boolean {
  return apiCategory(api) === 'external';
}

/** Une API MongoDB doit rester "ig" : l'explorateur Base de données lit son endpoint par env de jeu. */
export function canBeExternal(dockerImage: string | undefined): boolean {
  return !isMongoDbImage(dockerImage);
}

/** Image lue par l'explorateur Mongo : elle contient "mongodb", casse ignorée. */
export function isMongoDbImage(dockerImage: string | undefined): boolean {
  return (dockerImage ?? '').toLowerCase().includes('mongodb');
}

/** Nom de dépôt (dernier segment, sans tag ni digest) d'une image de moteur de base de données. */
const DATABASE_IMAGE_NAMES = new Set(['mongo', 'mongodb', 'postgres', 'postgresql', 'mysql', 'mariadb', 'redis', 'valkey']);

/**
 * API de base de données, miroir de ApiEnvs.isDatabase côté CICD : image Mongo de l'explorateur
 * (donc jamais externe), ou dépôt nommé comme un moteur connu ("mongo:8", ".../postgres:16").
 * Changer son image peut l'empêcher de redémarrer sur ses données : Release, « Déployer une
 * version… » et la création d'un serveur demandent de retaper son nom, la promotion l'ignore.
 */
export function isDatabaseImage(dockerImage: string | undefined): boolean {
  const img = (dockerImage ?? '').trim().toLowerCase();
  if (!img) return false;
  if (isMongoDbImage(img)) return true;
  const repo = img.split('@')[0].split('/').pop() ?? '';
  return DATABASE_IMAGE_NAMES.has(repo.split(':')[0]);
}

export function isDatabaseApi(api: Pick<ApiService, 'dockerImage'>): boolean {
  return isDatabaseImage(api.dockerImage);
}

/** Nom retapé pour confirmer un changement d'image d'une API de base de données (espaces autour ignorés). */
export function databaseNameMatches(api: Pick<ApiService, 'name'>, typed: string): boolean {
  return typed.trim() === api.name;
}

/** Code du 409 d'un CICD qui attend confirmDatabase (absent d'un CICD plus ancien, qui ignore le champ). */
export const DATABASE_CONFIRM_CODE = 'DATABASE_CONFIRM_REQUIRED';

/** Groupes (envs canoniques) où l'API tourne, d'après deployedVersions. */
export function deployedGroups(api: Pick<ApiService, 'deployedVersions'>): ApiEnvId[] {
  return API_ENV_GROUPS.filter((g) => apiEnvGroup(g).some((e) => !!api.deployedVersions?.[e]));
}

/** Version sans "v" initial, pour comparer un tag d'image à latestAvailable. */
export function normalizeVersion(v: string | null | undefined): string {
  return (v ?? '').trim().replace(/^v/i, '');
}

export function sameVersion(a: string | null | undefined, b: string | null | undefined): boolean {
  const na = normalizeVersion(a);
  return na !== '' && na === normalizeVersion(b);
}

/** "1.2.3-rc1" → { nums: [1, 2, 3], pre: "rc1" } ; null si ce n'est pas du semver (1 à 4 nombres). */
function parseVersion(v: string | null | undefined): { nums: number[]; pre: string } | null {
  const m = /^(\d+(?:\.\d+){0,3})(?:-([0-9A-Za-z.-]+))?(?:\+.*)?$/.exec(normalizeVersion(v));
  if (!m) return null;
  const nums = m[1].split('.').map(Number);
  while (nums.length < 4) nums.push(0);
  return { nums, pre: m[2] ?? '' };
}

/**
 * Ordre de deux versions : < 0 si a est plus ancienne, 0 si égales, > 0 si plus récente.
 * Une préversion (1.4.0-rc1) passe avant sa version finale (1.4.0). null si l'une ne se lit pas.
 */
export function compareVersions(a: string | null | undefined, b: string | null | undefined): number | null {
  const pa = parseVersion(a);
  const pb = parseVersion(b);
  if (!pa || !pb) return null;
  for (let i = 0; i < pa.nums.length; i++) {
    if (pa.nums[i] !== pb.nums[i]) return pa.nums[i] - pb.nums[i];
  }
  if (pa.pre === pb.pre) return 0;
  if (!pa.pre) return 1;
  if (!pb.pre) return -1;
  return pa.pre.localeCompare(pb.pre, undefined, { numeric: true });
}

/**
 * Position de la version déployée par rapport à la dernière release GitHub (qui exclut les
 * préversions) : "behind" = Release proposée, "ahead" = déployée plus récente (une Release
 * serait un downgrade), "same" = à jour. Tag illisible mais différent : traité comme un retard,
 * la confirmation et le libellé du bouton donnent la version visée.
 */
export function versionStatus(deployed: string | null | undefined, latest: string | null | undefined): 'behind' | 'ahead' | 'same' | null {
  if (!normalizeVersion(deployed) || !normalizeVersion(latest)) return null;
  if (sameVersion(deployed, latest)) return 'same';
  const c = compareVersions(deployed, latest);
  if (c === null) return 'behind';
  return c < 0 ? 'behind' : c > 0 ? 'ahead' : 'same';
}
